import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// models
import { FieldType } from '@karehero/models';

// components
import { Editor } from '@/components/organisms/Editor/Editor';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

interface Address {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postalCode?: string;
}

/**
 * AddressField allows you to add an address field to your form.
 */
export const AddressField = ({
  label,
  value,
  onChange,
  isDisabled,
  isRequired,
}: AddressFieldProps) => {
  // memo
  const contact = useMemo(() => {
    let c: Partial<Address> = {};
    try {
      c = JSON.parse(value || '{}');
    } catch (e) {
      console.error(e);
    }
    return c;
  }, [value]);

  const fields = useMemo(
    () =>
      createFieldHelper<Address>({
        addressLine1: {
          label: 'Address Line 1',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
        addressLine2: {
          label: 'Address Line 2',
          field: {
            type: FieldType.Text,
          },
        },
        addressLine3: {
          label: 'Address Line 3',
          field: {
            type: FieldType.Text,
          },
        },
        city: {
          label: 'City',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
        postalCode: {
          label: 'Postal Code',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
      }),
    [],
  );

  return (
    <Stack sx={{ gap: '16px' }}>
      <Box sx={{ typography: 'bodyLg', color: '#000000' }}>{label}</Box>
      <Editor
        fields={fields}
        value={contact}
        onChange={(val) => onChange(JSON.stringify(val))}
        isFloatingActions={false}
        isActionsDisabled={true}
        isForm={false}
        isDisabled={isDisabled}
      />
    </Stack>
  );
};

interface AddressFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
}

AddressField.displayName = 'AddressField';

export default AddressField;
